import type { ChauffeurPage } from "../slug-chauffeur";

import heroImage from "@/assets/images/chauffeur/cote-azur/chauffeur-cote-azur.png";

const niceCarnival: ChauffeurPage = {
  slug: "nice-carnival",
  title: "Private chauffeur Nice Carnival – families & groups VTC",
  subtitle:
    "Private chauffeur during the Nice Carnival: hotel and airport transfers despite the Promenade closures, drop-off near the parades and Riviera day trips.",
  keywords: [
    "nice carnival chauffeur",
    "nice carnival private chauffeur",
    "carnaval de nice chauffeur",
  ],
  hero: {
    title: "Private chauffeur during the Nice Carnival",
    subtitle:
      "In February, one of the world's great carnivals closes the Promenade and Place Masséna: a chauffeur who works around barriers and crowds for families and groups.",
    keywords: [
      "Nice Carnival chauffeur",
      "Promenade des Anglais VTC",
      "Place Masséna chauffeur",
    ],
    imageSrc: heroImage.src,
    imageAlt: "Private chauffeur during the Nice Carnival",
    badges: [
      "Promenade closures handled",
      "Drop-off near parades",
      "Families & groups",
    ],
  },
  benefits: {
    heading: "Why a chauffeur during the Carnival",
    items: [
      {
        title: "Promenade & Masséna closed",
        description:
          "The corsi and flower battles close the Promenade des Anglais and Place Masséna: the chauffeur knows the nearest authorised drop-offs to the stands.",
      },
      {
        title: "Crowds and saturated parking",
        description:
          "Central Nice is dense during the parades: skip the parking hassle with a drop-off and pick-up timed to the parade schedule.",
      },
      {
        title: "Families & groups",
        description:
          "Premium sedans and vans for families, children and groups of friends: the same chauffeur all day, no carrying strollers through the crowd.",
      },
      {
        title: "Airport close by",
        description:
          "Nice airport is minutes from the centre: welcome, flight tracking and a direct transfer to the hotel despite the carnival crowds.",
      },
    ],
  },
  services: {
    heading: "Chauffeur services during the Nice Carnival",
    services: [
      {
        title: "Nice airport transfer",
        text: "Airport ↔ hotel link in Nice with flight tracking, working around the Promenade closures on parade nights.",
      },
      {
        title: "Drop-off near the parades",
        text: "Approach as close as possible to Place Masséna and the stands, within the closed perimeter and the corsi schedule.",
      },
      {
        title: "Daily hire",
        text: "A dedicated chauffeur by the hour or day for families and groups, between hotel, parades and restaurants.",
      },
      {
        title: "Day trips around the Carnival",
        text: "Discovery days to Èze, Monaco, Cannes or the Menton Lemon Festival, often at the same time of year.",
      },
      {
        title: "Hotel & evening transfers",
        text: "Links between hotels, dinners and the evening illuminations, with a return after the night parades.",
      },
    ],
  },
  locations: {
    heading: "Key points during the Carnival",
    items: [
      {
        name: "Place Masséna",
        description:
          "The Carnival's heart: stands, the King's float and illuminated carnival corsi.",
      },
      {
        name: "Promenade des Anglais",
        description:
          "Seafront flower battles, closed to traffic during the parades.",
      },
      {
        name: "Nice Côte d'Azur airport",
        description:
          "Terminals 1 and 2, minutes from the centre via the Promenade or the Voie Mathis.",
      },
      {
        name: "Old Nice & port",
        description:
          "Hotels, restaurants and eastern access to the event area.",
      },
      {
        name: "Èze, Monaco & Menton",
        description:
          "Riviera day trips, including the Menton Lemon Festival alongside the Carnival.",
      },
    ],
  },
  generic: {
    heading: "The Nice Carnival from a chauffeur's seat",
    subheading:
      "In February, Nice closes the Promenade and Place Masséna for one of the world's great carnivals — dense crowds and reshaped traffic.",
    paragraphs: [
      "Every February, the Nice Carnival animates the city for two weeks: carnival corsi on Place Masséna and flower battles along the Promenade des Anglais. During the parades, these arteries are closed and the centre is saturated. A chauffeur who knows the authorised drop-offs gets you near the stands without the parking ordeal.",
      "Nice Côte d'Azur airport is minutes from the centre, but the Promenade closures complicate the links on parade nights. With flight tracking and an adapted route (Voie Mathis, eastern access), we secure a direct transfer to the hotel despite the crowds.",
      "The Carnival is a family-friendly, public event: premium sedans and vans for families, children and groups of friends, with the same chauffeur all day. And since February also brings the Lemon Festival to Menton, we happily combine a day trip to Menton, Monaco or Èze.",
    ],
    bullets: [
      "Drop-off nearest the stands",
      "Routes working around the Promenade",
      "Families, groups & day trips",
    ],
  },
  process: {
    heading: "Booking a chauffeur for the Nice Carnival",
    steps: [
      "Tell us your dates, your hotel and the parades (corsi, flower battles) you're aiming for.",
      "We plan the routes around the Promenade and Place Masséna closures.",
      "The chauffeur arrives in uniform, with flight tracking for your Nice arrivals.",
      "Hire for parades, dinners and day trips to Menton, Monaco or Èze.",
    ],
  },
  faq: [
    {
      q: "How do I book a chauffeur during the Nice Carnival?",
      a: "Book ahead: in February, the Carnival draws large crowds to Nice. Share your dates, your hotel and the parades you're aiming for so we can secure a dedicated chauffeur, suited to families and groups alike.",
    },
    {
      q: "Can you drop me near the parades despite the closures?",
      a: "Yes. During the corsi and flower battles, Place Masséna and the Promenade des Anglais are closed to traffic. The chauffeur drops you at the nearest authorised point to the stands within the perimeter and parade schedule, then collects you on the way out.",
    },
    {
      q: "How do you handle the airport when the Promenade is blocked?",
      a: "Nice airport is very close to the centre. On parade nights we use bypass routes (Voie Mathis, eastern access) and track your flight to secure a direct transfer to the hotel despite the closures.",
    },
    {
      q: "Do you offer day trips around the Carnival?",
      a: "Yes. The Carnival often coincides with the Menton Lemon Festival: on hourly hire, we organise days to Menton, Monaco, Èze or Cannes alongside the Nice parades.",
    },
  ],
  relatedLinks: [
    {
      label: "Nice Airport Saint-Jean-Cap-Ferrat route",
      href: "/en/chauffeur/transfers/nice-airport-saint-jean-cap-ferrat",
    },
    {
      label: "Monaco Nice Airport route",
      href: "/en/chauffeur/transfers/monaco-nice-airport",
    },
    {
      label: "Book a car with driver",
      href: "/en/reservation",
    },
  ],
  cta: {
    title: "Your chauffeur for the Nice Carnival",
    text: "Transfers despite the closures, drop-off near the parades and day trips: book a dedicated chauffeur for the Carnival.",
    primaryHref: "/en/reservation",
    primaryLabel: "Book a chauffeur",
  },
  seo: {
    title: "Nice Carnival — family car service, Promenade & Masséna",
    description:
      "Private chauffeur for the Nice Carnival: hotel and airport transfers despite Promenade closures, drop-off near the parades, family hire and day trips.",
    canonical: "/en/chauffeur/nice-carnival",
  },
};

export default niceCarnival;
